import { FC, useMemo, useRef } from 'react';

import { formatCostInCents } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { FIRST_CAMPAIGN_AUCTION_PROMO, getClickmePromoDiscountedPrice } from 'utils/promoBonus';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import auctionPromoElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_promo_element_shown';
import { ActionBar, BottomSheet, Button, Card, Flex, Text, TooltipHover, VSpacingContainer } from '@hh.ru/magritte-ui';
import { InfoCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import { useAvailableFirstCampaignAuctionPromo } from 'hooks/useAvailableFirstCampaignAuctionPromo';

import FormatMoney from 'components/FormatMoney';

import { DiscountedPrice } from 'components/common/DiscountedPrice/DiscountedPrice';

import { AuctionDataField, auctionSelector, auctionSelectorNonNullable } from 'store/models/auctionData';
import { useSelector } from 'store/utils';

const TrlKeys = {
    label: 'AuctionBuilder.AuctionBudget.AuctionTotal.label',
    tooltip: {
        part1: 'AuctionBuilder.AuctionActions.AuctionTotal.tooltip.part1',
        part2: 'AuctionBuilder.AuctionActions.AuctionTotal.tooltip.part2',
        moreButton: 'AuctionBuilder.AuctionActions.AuctionTotal.tooltip.more',
    },
    promoPercent: 'AuctionBuilder.AuctionActions.AuctionTotal.promoPercent',
};

export const AuctionTotal: FC = () => {
    const auctionBudget = useSelector(auctionSelectorNonNullable[AuctionDataField.Budget]).value;
    const vacancyId = useSelector(auctionSelector[AuctionDataField.VacancyId]);
    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const activatorRef = useRef(null);
    const trls = useTrls();

    const { maxBonusAmount, promoSumPercent, termsLink } = useAvailableFirstCampaignAuctionPromo();

    const discountedPrice = useMemo(
        () => getClickmePromoDiscountedPrice(auctionBudget || 0, promoSumPercent, maxBonusAmount),
        [auctionBudget, promoSumPercent, maxBonusAmount]
    );

    const tooltipContent = maxBonusAmount && promoSumPercent && (
        <VSpacingContainer default={8}>
            <Text>{trls(TrlKeys.tooltip.part1)}</Text>
            <Text>
                {trls(TrlKeys.tooltip.part2, [promoSumPercent, formatCostInCents(maxBonusAmount, NON_BREAKING_SPACE)])}
            </Text>
        </VSpacingContainer>
    );

    if (!auctionBudget) {
        return null;
    }

    return (
        <>
            <Card padding={16} borderRadius={16} borderWidth="default" stretched data-qa="auction-total">
                <Flex
                    direction={{ xs: 'column', s: 'row' }}
                    align={{ xs: 'flex-start', s: 'center' }}
                    justify="space-between"
                    gap={12}
                    width="100%"
                >
                    <Text typography="label-3-regular" style="secondary">
                        {trls(TrlKeys.label)}
                    </Text>
                    <ElementShownAnchor
                        fn={auctionPromoElementShown}
                        vacancyId={vacancyId}
                        promoKey={FIRST_CAMPAIGN_AUCTION_PROMO}
                        triggerType="PRICE_BLOCK"
                    >
                        <Flex direction="row" align="center" gap={16}>
                            <Flex direction="row" align="center" gap={8}>
                                <Text typography="label-3-regular" style="secondary">
                                    <DiscountedPrice>
                                        <FormatMoney inCents groupSeparator={NON_BREAKING_SPACE}>
                                            {auctionBudget}
                                        </FormatMoney>
                                    </DiscountedPrice>
                                </Text>
                                <Flex direction="row" align="center" gap={2}>
                                    <Text style="positive" typography="label-3-regular">
                                        {trls(TrlKeys.promoPercent, [promoSumPercent])}
                                    </Text>
                                    {tooltipContent && (
                                        <InfoCircleOutlinedSize16
                                            ref={activatorRef}
                                            initialColor="positive"
                                            onClick={setIsTooltipVisibleOn}
                                        />
                                    )}
                                </Flex>
                            </Flex>
                            <Text typography="subtitle-1-semibold" data-qa="auction-total-price">
                                <FormatMoney inCents groupSeparator={NON_BREAKING_SPACE}>
                                    {discountedPrice}
                                </FormatMoney>
                            </Text>
                        </Flex>
                    </ElementShownAnchor>
                </Flex>
            </Card>

            <TooltipHover
                activatorRef={activatorRef}
                placement="top-center"
                buttons={
                    termsLink ? (
                        <Button mode="primary" style="constant" Element="a" target="_blank" href={termsLink}>
                            {trls(TrlKeys.tooltip.moreButton)}
                        </Button>
                    ) : undefined
                }
            >
                {tooltipContent}
            </TooltipHover>
            <BottomSheet
                onClose={setIsTooltipVisibleOff}
                visible={isTooltipVisible}
                footer={
                    termsLink ? (
                        <ActionBar
                            padding={16}
                            primaryActions={
                                <Button mode="secondary" style="neutral" Element="a" target="_blank" href={termsLink}>
                                    {trls(TrlKeys.tooltip.moreButton)}
                                </Button>
                            }
                        />
                    ) : undefined
                }
            >
                <Text typography="label-1-regular">{tooltipContent}</Text>
            </BottomSheet>
        </>
    );
};
